import React from 'react';
import { BaseResourceListProps, FilterButton, Form, SimpleList } from '@app/components';
import { fiscalApi, selectAuthenticatedUser, serviceApi } from '@app/store';
import { convertToEuros, IService } from '@app/common';
import { theme } from '@app/theme';
import { createColumnHelper } from '@tanstack/react-table';
import { useTable } from '@app/hooks/useTable';
import { DateTime } from 'luxon';
import { SvgEye } from '@app/components/icons/SvgEye';
import { DateTimePicker } from '@app/components/form/DateTimePicker.web';
import { row } from '@app/style';
import { extractApiGenericError, normalize } from '@app/helpers';
import styled from '@2pulse/responsive';
import { useForm } from 'react-hook-form';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { useToast } from '@app/hooks';
import { useAppSelector, useAppDispatch } from '@app/store/hooks';
import {
  DetailRefusExportComptable,
  detailRefusExportComptable,
  estRefusExportComptableStructure,
  messageExportScelle,
  titreRefusExportComptable,
} from './service-list-export.logic';

const columnHelper = createColumnHelper<IService>();

const getStartOfDayISO = (date: string | null) => {
  return date ? DateTime.fromISO(date).startOf('day').toISO() : null;
};

const getEndOfDayISO = (date: string | null) => {
  return date ? DateTime.fromISO(date).endOf('day').toISO() : null;
};

const startOfTodayISO = DateTime.now().endOf('day').toISO();
const oneMonthAgoISO = DateTime.now().minus({ months: 1 }).startOf('day').toISO();

export const ServiceList = ({ onShow, onRowPress, selectedRowItem }: Omit<BaseResourceListProps<IService>, 'onUpdate'> & { onShow: (item: IService) => void }) => {
  const toast = useToast();

  const columns = [
    columnHelper.accessor('shop.publicName', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Restaurant</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
                {info.getValue()}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('fromDate', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Date de début</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
                {DateTime.fromISO(info.getValue()).toFormat('dd/MM/yyyy HH:mm')}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('toDate', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>Date de fin</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
                {DateTime.fromISO(info.getValue()).toFormat('dd/MM/yyyy HH:mm')}
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('caHT', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>CA HT</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="center" bold numberOfLines={2} active={info.active}>
                {convertToEuros(info.getValue()).toFixed(2).replace('.', ',')} €
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.accessor('caTTC', {
      header: () => (
        <SimpleList.HeaderItem width="15%">
          <SimpleList.HeaderItemLabel>CA TTC</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => {
        return (
          <SimpleList.ItemCol width="15%">
            <SimpleList.ItemImageWrapper>
              <SimpleList.ItemText align="center" bold numberOfLines={2} active={info.active}>
                {convertToEuros(info.getValue()).toFixed(2).replace('.', ',')} €
              </SimpleList.ItemText>
            </SimpleList.ItemImageWrapper>
          </SimpleList.ItemCol>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <SimpleList.HeaderItem width="15%" align='center'>
          <SimpleList.HeaderItemLabel>Actions</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => (
        <SimpleList.ItemCol width="15%">
          <SimpleList.ItemActions>
            <CheckPermissions permissions={['dashboard::services.read']}>
              <SimpleList.ItemAction onPress={() => onShow(row.original)}>
                <SvgEye color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>
          </SimpleList.ItemActions>
        </SimpleList.ItemCol >
      ),
    }),
  ];

  const formMethods = useForm<{
    fromDate: string | null;
    toDate: string | null;
  }>({
    defaultValues: {
      fromDate: oneMonthAgoISO,
      toDate: startOfTodayISO
    }
  });

  const selectedShop = useAppSelector((state) => state.app.selectedShop);
  const dispatch = useAppDispatch();
  // Auteur du téléchargement (courriel), joint au fichier compagnon `…-scellement.json`
  // (E-07, tâche 14) — connu côté client, jamais attesté par le sceau lui-même (qui ne porte
  // que sur le CSV).
  const utilisateur = useAppSelector(selectAuthenticatedUser);
  // Détail STRUCTURÉ d'un refus de rapprochement (E-07, tâche 12/14) : registre, clôture,
  // écart en euros — jamais une bannière générique pour ces trois codes.
  const [refusExport, setRefusExport] = React.useState<DetailRefusExportComptable | null>(null);

  const [fromDate, toDate] = formMethods.watch(['fromDate', 'toDate']);

  const [generateSummary, { isLoading }] = serviceApi.useGenerateSummaryMutation();
  const [generateCsvSummary, { isLoading: isLoadingCsv }] = serviceApi.useGenerateCsvSummaryMutation();

  const fromDateISO = getStartOfDayISO(fromDate);
  const toDateISO = getEndOfDayISO(toDate);

  const { table, setSearch, isFetching } = useTable({
    columns,
    useQuery: ({ pagination }) => serviceApi.useReadManyServicesQuery({
      pagination,
      filters: {
        $and: [
          ...(!!fromDateISO ? [{
            fromDate: {
              $gte: fromDateISO
            }
          }] : []),
          ...(!!toDateISO ? [{
            toDate: {
              $lte: toDateISO
            }
          }] : []),
          ...(!!selectedShop ? [{
            shop: {
              _id: {
                $eq: selectedShop._id
              }
            }
          }] : []),
        ]
      },
      sort: ['fromDate:DESC'],
    })
  });

  const isDownloadDisabled = !fromDateISO || !toDateISO;

  const downloadSummary = async () => {
    if (isDownloadDisabled) {
      return;
    }

    if (!selectedShop) {
      alert('Veuillez sélectionner un restaurant dans le menu latéral.');
      return;
    }

    try {
      await generateSummary({
        startDate: fromDateISO!,
        endDate: toDateISO!,
        shop: selectedShop,
        withDetails: true,
      }).unwrap();
    } catch (error: any) {
      const errorMessage = extractApiGenericError(error) ?? 'Une erreur est survenue lors du téléchargement du résumé.';
      toast.error(errorMessage);
    }
  }

  const downloadCsvSummary = async () => {
    if (isDownloadDisabled) {
      return;
    }

    if (!selectedShop) {
      alert('Veuillez sélectionner un restaurant dans le menu latéral.');
      return;
    }

    setRefusExport(null);

    try {
      const resultat = await generateCsvSummary({
        startDate: fromDateISO!,
        endDate: toDateISO!,
        shop: selectedShop,
        userEmail: utilisateur?.email,
      }).unwrap();

      // Le sceau retourné par l'export réussi (tâche 13) : scellé (rapproché) ou non
      // (boutique sans registre, jamais un refus) — jamais silencieux sur lequel des deux.
      const messageSceau = messageExportScelle((resultat as { sceau?: unknown })?.sceau as never);
      if (messageSceau) toast.success(messageSceau);
    } catch (error: any) {
      // T12's structured 422 (`{ data: { code, manquants | ecarts | nonAlignes } }`, porté
      // par le `GlobalExceptionFilter` — même convention que `CatalogPublishModal.tsx`,
      // `error?.data?.data?.message`) : le détail structuré remplace la bannière générique,
      // jamais l'inverse.
      const detail = (error as any)?.data?.data;

      if (estRefusExportComptableStructure(detail)) {
        setRefusExport(detail);
        // Fix round 1 (revue, E-07) : un refus 422 écrit AUSSI une ligne d'audit
        // (best-effort, tâche 12/13) — mais `generateCsvSummary` rejette ici, RTK Query
        // n'invalide `invalidatesTags` que sur une mutation `fulfilled`. Sans ce refetch
        // explicite, la page Conformité resterait périmée jusqu'à un remontage fortuit.
        dispatch(fiscalApi.util.invalidateTags([{ type: 'FiscalAccountingExports', id: 'LIST' }]));
        return;
      }

      const errorMessage = extractApiGenericError(error) ?? 'Une erreur est survenue lors du téléchargement du résumé CSV.';
      toast.error(errorMessage);
    }
  }

  return (
    <SimpleList
      table={table}
      isFetching={isFetching}
      onSearch={setSearch}
      selectedRowItem={selectedRowItem}
      onRowPress={onRowPress}
      SearchComponent={(
        <Form formMethods={formMethods}>
          <FiltersContainer>
            <DateTimePicker
              name="fromDate"
              type="date"
              label="Date de début"
              onWhite />

            <DateTimePicker
              name="toDate"
              type="date"
              label="Date de fin"
              onWhite />
            <FiltersSeparator />

            <CheckPermissions permissions={['dashboard::services.generate-summary']}>
              <FilterButton
                title="Export PDF"
                disabled={isDownloadDisabled}
                loading={isLoading}
                onPress={downloadSummary} />
            </CheckPermissions>

            <CheckPermissions permissions={['dashboard::services.generate-csv-summary']}>
              <FilterButton
                title="Export CSV"
                disabled={isDownloadDisabled}
                loading={isLoadingCsv}
                onPress={downloadCsvSummary} />
            </CheckPermissions>
          </FiltersContainer>

          {/* Refus 422 STRUCTURÉ du rapprochement comptable (E-07, tâche 12/14) : le détail
              (registre, clôture, écart en euros) remplace la bannière générique du toast pour
              ces trois codes — jamais l'inverse. */}
          {refusExport && (
            <RefusExportBanner testID="refus-export-comptable">
              <RefusExportTitre>{titreRefusExportComptable(refusExport)}</RefusExportTitre>
              {detailRefusExportComptable(refusExport).map((ligne, index) => (
                <RefusExportLigne key={index}>{ligne}</RefusExportLigne>
              ))}
            </RefusExportBanner>
          )}
        </Form>
      )} />
  )
};

const FiltersContainer = styled.View({
  ...row,
  width: '100%',
  paddingLeft: 106,
  paddingRight: normalize(21),
  columnGap: normalize(13),
  paddingBottom: normalize(8),
  paddingTop: normalize(8),
  rowGap: normalize(8),
  flexWrap: 'wrap',
  alignItems: 'center',
});

const FiltersSeparator = styled.View({
  flexGrow: 1,
});

const RefusExportBanner = styled.View(({ theme }) => ({
  width: '100%',
  marginLeft: 106,
  marginRight: normalize(21),
  marginBottom: normalize(13),
  borderRadius: normalize(20),
  borderWidth: 1,
  borderColor: theme.colors.crimson,
  backgroundColor: 'rgba(220, 0, 0, 0.1)',
  paddingHorizontal: normalize(18),
  paddingVertical: normalize(13),
  rowGap: normalize(6),
}));

const RefusExportTitre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.crimson,
}));

const RefusExportLigne = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.crimson,
}));
